import type { Translations } from "./types";

export const en: Translations = {
  // Login
  "login.title": "Tessa",
  "login.email": "Email",
  "login.password": "Password",
  "login.submit": "Log in",
  "login.submitting": "Logging in...",
  "login.error": "Invalid email or password",

  // Home / Projects page
  "home.projects": "Projects",
  "home.searchPlaceholder": "Search projects",
  "home.createNew": "Create New Project",
  "home.newProject": "New Project",
  "home.projectName": "Project name",
  "home.cancel": "Cancel",
  "home.createProject": "Create Project",
  "home.savedDesigns": "Saved designs",
  "home.dateCreated": "Date Created:",
  "home.lastModified": "Last Modified:",
  "home.noResultsTitle": "No results found",
  "home.noResultsMessage": "We couldn't find a match for {{search}}. Please try another search.",
  "home.loadingProjects": "Loading projects",

  // Navigation
  "nav.settings": "Settings",
  "nav.editProject": "Edit Project",
  "nav.costSettings": "Cost settings",
  "nav.deleteProject": "Delete project",
  "nav.logout": "Log out",
  "nav.admin": "Admin",
  "nav.canvas": "Canvas",
  "nav.studio": "Studio",
  "nav.savedDesigns": "SAVED DESIGNS",
  "nav.noSavedDesigns": "No Saved Designs",
  "nav.openInCanvas": "Open in Canvas",
  "nav.rename": "Rename",

  // Common
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.saved": "Saved",
  "common.delete": "Delete",
  "common.deleted": "Deleted",
  "common.edit": "Edit",
  "common.update": "Update",
  "common.search": "Search",
  "common.loading": "Loading",
  "common.total": "Total",
  "common.required": "Required project name",
  "common.continue": "Continue",
  "common.name": "Name",

  // Delete project modal
  "deleteProject.title": "Delete",
  "deleteProject.message":
    "This will delete the project permanently. This action cannot be undone.",

  // Delete design modal
  "deleteDesign.title": "Delete {{name}}?",
  "deleteDesign.message": "This will delete the design permanently. This action cannot be undone.",

  // Update project modal
  "updateProject.title": "Edit Project",
  "updateProject.projectName": "Project name",
  "updateProject.notes": "Notes",
  "updateProject.maxChars": "Maximum 120 characters",
  "updateProject.placeholder": "Type here...",

  // Cost settings
  "cost.title": "Cost Settings",
  "cost.infoMessage":
    "Adjustments to cost settings will be reflected across all Design Options within this project.",
  "cost.prelims": "Prelims",
  "cost.prelimsDesc": "applied to Construction cost",
  "cost.contingency": "Contingency",
  "cost.contingencyDesc": "applied to Construction and Prelims costs",
  "cost.ohp": "OH&P Allowance",
  "cost.ohpDesc": "applied to Construction, Prelims and Contingency costs",

  // Inspector
  "inspector.title": "Inspector",
  "inspector.seeAllMetrics": "See all metrics",

  // Design metrics
  "design.title": "Design",
  "design.residentialEfficiency": "Residential efficiency",
  "design.buildingEfficiency": "Building efficiency",
  "design.floors": "Floors",
  "design.buildingHeight": "Building height",
  "design.ffl": "FFL",
  "design.podiumHeight": "Podium height",
  "design.gia": "GIA",
  "design.nia": "NIA",
  "design.buildingFootprint": "Building footprint",
  "design.densityUnits": "Density (units)",
  "design.landCoverage": "Land coverage",
  "design.northFacingUnits": "North facing units",
  "design.grossFacadeArea": "Gross façade area",
  "design.commercialArea": "Commercial area",
  "design.amenitiesArea": "Amenities area",
  "design.servicesArea": "Services area",
  "design.grz": "GRZ",
  "design.gfz": "GFZ",

  // Cost metrics
  "costMetrics.title": "Cost",
  "costMetrics.modules": "Modules",
  "costMetrics.fitOut": "Fit-out",
  "costMetrics.baseBuild": "Base build",
  "costMetrics.construction": "Construction",
  "costMetrics.constructionPerUnit": "Construction /unit",
  "costMetrics.constructionPerArea": "Construction /area",
  "costMetrics.perUnit": "/unit",
  "costMetrics.perSqm": "/sqm",
  "costMetrics.perSqft": "/sqft",

  // Sustainability metrics
  "sustainability.title": "Sustainability",
  "sustainability.indicativeFormFactor": "Indicative form factor",
  "sustainability.co2Footprint": "CO₂ footprint",
  "sustainability.co2FootprintPerArea": "CO₂ footprint /area",
  "sustainability.superstructure": "Superstructure",
  "sustainability.superstructureCo2": "Superstructure CO₂ footprint",
  "sustainability.superstructureCo2PerArea": "Superstructure CO₂ footprint /area",
  "sustainability.substructure": "Substructure",
  "sustainability.substructureCo2": "Substructure CO₂ footprint",
  "sustainability.substructureCo2PerArea": "Substructure CO₂ footprint /area",
  "sustainability.steelCo2": "Steel CO₂ footprint",
  "sustainability.steelCo2PerArea": "Steel CO₂ footprint /area",
  "sustainability.concreteCo2": "Concrete CO₂ footprint",
  "sustainability.concreteCo2PerArea": "Concrete CO₂ footprint /area",

  // Zones
  "zones.title": "Zones",
  "zones.type": "Zone type",
  "zones.buildable": "Buildable",
  "zones.exclusion": "Exclusion",
  "zones.parking": "Parking",
  "zones.access": "Access",
  "zones.greenZones": "Green Zones",
  "zones.area": "Area:",
  "zones.noBuildings": "No buildings in zone.",
  "zones.tooSmall": "It may be too small.",

  // Units (measurement system)
  "units.title": "Units",
  "units.total": "Total:",
  "units.metric": "Metric",
  "units.imperial": "Imperial",
  "units.sqm": "sqm",
  "units.sqft": "sqft",
  "units.m": "m",
  "units.ft": "ft",
  "units.kg": "kg",
  "units.kgSqm": "kg/sqm",
  "units.kgSqft": "kg/sqft",
  "units.ha": "ha",
  "units.ac": "ac",
  "units.floors": "floors",

  // Brief panel
  "brief.title": "Brief",
  "brief.site": "Site",
  "brief.buildingSeparation": "Building Separation",
  "brief.default": "Default",
  "brief.custom": "Custom",
  "brief.regulationsMayApply": "Regulations may apply",
  "brief.residentialFloors": "Residential floors",

  // Residential unit types (used in Brief unit mix)
  "unitType.1b2p": "1B2P",
  "unitType.2b3p": "2B3P",
  "unitType.2b4p": "2B4P",
  "unitType.3b5p": "3B5P",
  "unitType.3b6p": "3B6P",

  // Custom service areas
  "serviceAreas.title": "Custom Service Areas",
  "serviceAreas.energyGeneration": "Energy generation",
  "serviceAreas.ambientLoop": "Ambient loop network district heating scheme",
  "serviceAreas.networkDistrict": "Network district heating scheme",
  "serviceAreas.localHeatPumps": "Local heat pumps",
  "serviceAreas.electricalPower": "Electrical power requirements",
  "serviceAreas.plvOnly": "Primary low voltage (PLV) room only",
  "serviceAreas.plvSecondary": "PLV + secondary low voltage room",
  "serviceAreas.plvGenerator": "PLV + standby generator",
  "serviceAreas.extras": "Extras",
  "serviceAreas.sprinklers": "Sprinklers",
  "serviceAreas.electricalSubstation": "Electrical substation",
  "serviceAreas.rainwaterHarvesting": "Rainwater harvesting system",
  "serviceAreas.greywaterHarvesting": "Greywater harvesting system",

  // Podium
  "podium.title": "Podium",
  "podium.use": "Use",
  "podium.commercial": "Commercial",
  "podium.levels": "Levels",
  "podium.height": "Height",

  // Facade
  "facade.title": "Façade Finish Material",
  "facade.traditionalBrickwork": "Traditional Brickwork",
  "facade.precastConcrete": "Precast Concrete Panels",
  "facade.fibreCement": "Fibre/Cement Cladding System",
  "facade.mechanicalBrickSlip": "Mechanical BrickSlip System",
  "facade.metalCladding": "Metal Cladding System",

  // Metrics drawer
  "metrics.title": "Metrics",
  "metrics.areaMeasurements": "Area measurements",
  "metrics.level": "Level",
  "metrics.gea": "GEA",
  "metrics.efficiency": "Efficiency",
  "metrics.unitMix": "Unit Mix",
  "metrics.type": "Type",
  "metrics.avgArea": "Avg area",
  "metrics.quantity": "Quantity",
  "metrics.mix": "Mix",
  "metrics.nonResidentialAreas": "Non-residential areas",
  "metrics.area": "Area",
  "metrics.footprint": "Footprint",
  "metrics.commercials": "Commercials - ",
  "metrics.services": "Services - ",
  "metrics.ground": "Ground",
  "metrics.amenities": "Amenities - ",
  "metrics.nonResidentialNote":
    "Total non-residential area divided by total GEA across all floors.",
  "metrics.showMeasurementsIn": "Show measurements in",
  "metrics.blocksUnits": "Units",
  "metrics.blocksModules": "Modules",

  // Floor labels (used in metrics drawer)
  "floor.ground": "Ground Floor",
  "floor.1st": "1st Floor",
  "floor.2nd": "2nd Floor",
  "floor.3rd": "3rd Floor",
  "floor.nth": "{{n}}th Floor",
  "floor.roof": "Roof",
  "floor.short.ground": "Ground",
  "floor.short.1st": "1st",
  "floor.short.2nd": "2nd",
  "floor.short.3rd": "3rd",
  "floor.short.nth": "{{n}}th",

  // Block labels
  "block.label": "Block {{letter}}",

  // Cost summary
  "costSummary.title": "Summary",
  "costSummary.cost": "Cost",
  "costSummary.perUnit": "Per unit",
  "costSummary.per": "Per ",
  "costSummary.modular": "Modular",
  "costSummary.fitOut": "Fit out",
  "costSummary.baseBuild": "Base Build",
  "costSummary.construction": "Construction",
  "costSummary.prelims": "Prelims",
  "costSummary.contingency": "Contingency",
  "costSummary.ohp": "OH&P allowance",
  "costSummary.prelimsTooltip":
    "Default value is 10% applied to Construction cost. Go to Cost Settings to adjust.",
  "costSummary.contingencyTooltip":
    "Default value is 3% applied to Construction and Prelims costs. Go to Cost Settings to adjust.",
  "costSummary.ohpTooltip":
    "Default value is 5.5% applied to Construction, Prelims and Contingency costs. Go to Cost Settings to adjust.",
  "costSummary.totalTooltip": "Total cost is the sum of all costs. Go to Cost Settings to adjust.",
  "costSummary.moduleElement": "Module element",

  // Sustainability summary
  "sustainSummary.title": "Summary",
  "sustainSummary.footprint": "footprint",
  "sustainSummary.footprintPer": "footprint per ",
  "sustainSummary.substructure": "Substructure",
  "sustainSummary.superstructure": "Superstructure",
  "sustainSummary.other":
    "Other (Internal finishes & furniture; Fittings, furnishings & equipment; Services.)",
  "sustainSummary.carbonNote":
    "We are working on accurately measuring the carbon footprint of the substructure, which will be included in the next release of TESSA",

  // Explore panel
  "explore.designOptions": "Design Options",
  "explore.of": "of",
  "explore.columns": "Columns",
  "explore.chooseColumns": "Choose columns",
  "explore.resetToDefault": "Reset to default",
  "explore.selected": "Selected",
  "explore.noDesignOptions": "There are no design options available",
  "explore.basedOnInputs": "based on zone and brief inputs",
  "explore.pin": "Pin",
  "explore.design": "Design",
  "explore.quantity": "Quantity",
  "explore.measurement": "Measurement",
  "explore.calculated": "Calculated",
  "explore.constructionCost": "Construction cost",
  "explore.totalCost": "Total cost",
  "explore.outputs": "Outputs",
  "explore.embodiedCarbonPerArea": "Embodied carbon per area",
  "explore.totalEmbodiedCarbon": "Total embodied carbon",
  "explore.operationalCarbon": "Operational carbon",

  // Explore table columns
  "column.name": "Name",
  "column.image": "Image",
  "column.units": "Units",
  "column.buildings": "Buildings",
  "column.modules": "Modules",
  "column.floors": "Floors",
  "column.floorsResidential": "Floors (residential)",
  "column.podiumLevels": "Podium levels",
  "column.cores": "Cores",
  "column.liftCores": "Lift cores",
  "column.firefightingCores": "Firefighting cores",
  "column.entrances": "Entrances",
  "column.unitsPerCore": "Units per core",
  "column.dualAspectUnits": "Dual aspect units",
  "column.partMCompliantUnits": "Part M compliant units",
  "column.density": "Density",
  "column.densityHabitableRooms": "Density (habitable rooms)",
  "column.wallToFloorRatio": "Wall to floor ratio",
  "column.coreAndCorridorArea": "Core & corridor area",
  "column.nonResidentialArea": "Non-residential area",
  "column.serviceArea": "Services area",
  "column.roofArea": "Roof area",
  "column.glazedFacadeArea": "Glazed façade area",
  "column.netFacadeArea": "Net façade area",
  "column.concaveFacadeCorner": "Concave façade corner length",
  "column.convexFacadeCorner": "Convex façade corner length",

  // Editor
  "editor.3d": "3D",
  "editor.2d": "2D",
  "editor.labels": "Labels",
  "editor.2dBlockView": "2D Block View",
  "editor.allZones": "All zones",
  "editor.allBlocks": "All blocks",
  "editor.legend": "Legend",
  "editor.residentialAreas": "RESIDENTIAL AREAS",
  "editor.nonResidentialAreas": "NON-RESIDENTIAL AREAS",
  "editor.compass": "N",

  // Canvas toolbox
  "toolbox.back": "Back",
  "toolbox.forward": "Forward",
  "toolbox.undo": "Undo",
  "toolbox.redo": "Redo",
  "toolbox.create": "Create",
  "toolbox.cut": "Cut",
  "toolbox.merge": "Merge",
  "toolbox.delete": "Delete",
  "toolbox.edgeOffset": "Edge Offset",
  "toolbox.save": "Save",
  "toolbox.export": "Export",
  "toolbox.exportBrief": "Brief snapshot",
  "toolbox.exportFull": "Full snapshot",
  "toolbox.summaryPdf": "Summary PDF",
  "toolbox.select": "Select",
  "toolbox.search": "Search",

  // Map view (canvas map layer selector)
  "mapView.title": "Map View",
  "mapView.map": "Map",
  "mapView.abstract": "Abstract",
  "mapView.architectural": "Architectural",
  "mapView.custom": "Custom",
  "mapView.light": "Light",
  "mapView.satellite": "Satellite",
  "mapView.street": "Street",
  "mapView.siteContext": "Site Context",
  "mapView.topography": "Topography lines",
  "mapView.zones": "Zones",

  // Stencil overlay (canvas map image overlay)
  "stencil.title": "Stencil overlay",
  "stencil.upload": "Upload PNG",
  "stencil.uploadFile": "Upload PNG or WMS XML",
  "stencil.opacity": "Opacity",
  "stencil.remove": "Remove",
  "stencil.lock": "Lock to map",
  "stencil.unlock": "Unlock",
  "stencil.wmsLayer": "WMS layer",
  "stencil.error.invalidWms": "Unsupported WMS file format",
  "stencil.error.unsupportedFile": "Unsupported file type",
  "stencil.error.tileLoad": "Could not load WMS tiles. The server may not allow CORS or EPSG:3857.",
  "stencil.error.fetchFailed":
    "Could not fetch from URL. The server may not allow CORS, or the URL is wrong.",
  "stencil.placementTitle": "Position the stencil",
  "stencil.cancel": "Cancel",
  "stencil.size": "Size",
  "stencil.editMode": "Edit Mode",
  "stencil.done": "Done",
  "stencil.catalogDefaults": "Default layers",
  "stencil.catalogSaved": "Saved on this project",
  "stencil.searchPlaceholder": "Search layers…",
  "stencil.noResults": "No layers match",
  "stencil.active": "Active",
  "stencil.cat.land-registry": "Land registry",
  "stencil.cat.planning-law": "Planning law",
  "stencil.cat.brw": "Land value (BRW)",
  "stencil.addCustom": "Add custom layer",
  "stencil.activeOverlay": "Active overlay",
  "stencil.tabFile": "File",
  "stencil.tabUrl": "URL",
  "stencil.urlPlaceholder": "https://example.com/wms",
  "stencil.urlLoad": "Load",
  "stencil.removeSaved": "Remove from saved",

  // Building Presets panel
  "presets.title": "Building Presets",
  "presets.unit": "Unit",
  "presets.cores": "Cores",
  "presets.corridors": "Corridors",
  "presets.services": "Services",
  "presets.amenities": "Amenities",
  "presets.entrances": "Entrances",
  "presets.facade": "Facade",
  "presets.roof": "Roof",
  "presets.parapets": "Parapets",
  "presets.floors": "Floors",
  "presets.ceilings": "Ceilings",
  "presets.walls": "Walls",
  "presets.windows": "Windows",
  "presets.doors": "Doors",
  "presets.structure": "Structure",
  "presets.commercials": "Commercials",
  "presets.floorLevels": "Floor Levels",

  // Plan2D room types
  "roomType.corridor": "Corridor",
  "roomType.core": "Core",
  "roomType.entrance": "Entrance",
  "roomType.amenity": "Amenity",
  "roomType.service": "Service",
  "roomType.commercialSpace": "Commercial Space",

  // Currency settings
  "currency.title": "Currency Symbol",
  "currency.description":
    "Override the currency symbol shown throughout the app. The underlying cost data is unchanged — this is for demo purposes only.",
  "currency.label": "Currency",
  "currency.eur": "EUR — €",
  "currency.gbp": "GBP — £",
  "currency.storedNote":
    "Stored in localStorage. Changes take effect immediately across all open tabs.",

  // Language
  "language.title": "Language",
  "language.en": "English",
  "language.de": "Deutsch",

  // Save Design modal
  "saveModal.title": "Save Design",

  // Export modal
  "export.title": "Export",
  "export.saveAndExport": "Save & Export",
  "export.bimPackage": "BIM Revit 2021 package",
  "export.metricsLabel": "Metrics",
  "export.mustSaveNotice": "You must save this design to export",
  "export.creditsRequired": "Exporting this file requires {{count}} credits.",
  "export.creditsCurrent": "You currently have {{count}} credits.",
  "export.creditUnit": "1 unit = 1 credit.",
  "export.emailNotice":
    "We will email {{email}} with a link to download your files when they are ready.",
  "export.allZones": "All zones",
  "export.allBuildings": "All buildings",
  "export.areaSection": "Area",
  "export.zoneLabel": "Zone",
  "export.buildingLabel": "Building",
  "export.designOption": "Design option #{{n}}",
  "export.blocksSelected": "blocks selected",

  // Override canvas design modal
  "overrideCanvas.title": "Open in Canvas",
  "overrideCanvas.message":
    "This will replace the zones and design brief in the Canvas with those from {{name}}. Do you wish to continue?",
};
